// React
import { useEffect, useState } from 'react'

// Mantine
import { Modal, Button, Anchor, LoadingOverlay } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { DatePickerInput } from '@mantine/dates'

// Axios
import axios from 'axios'
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Daysjs
import dayjs from 'dayjs'

// Lodash
import orderBy from 'lodash/orderBy'

export default function DispatchDateEdit({
    fullfillmentOrdersDate,
    location_id,
    onDateChange,
}) {
    // Modal state
    const [opened, handlers] = useDisclosure(false)
    const [isLoading, setIsLoading] = useState(false)

    // Variant selection
    const [targetVariants, setTargetVariants] = useState([])
    const [targetComponents, setTargetComponents] = useState([])

    // Form value
    const [targetDateValue, setTargetDateValue] = useState(null)

    const [sourceDateGroup, setSourceDateGroup] = useState(
        fullfillmentOrdersDate
    )
    const [targetDateGroup, setTargetDateGroup] = useState(null)

    const openModal = () => {
        setSourceDateGroup(fullfillmentOrdersDate)
        setTargetDateGroup(null)
        setTargetVariants([])
        setTargetComponents([])
        setTargetDateValue(null)

        handlers.open()
    }

    // Fetch fulfillment orders simulations on selected variants change
    const { requestPost, loading } = useAxiosApi()

    const fetchSimulations = async (newTargetVariants) => {
        setIsLoading(true)

        const { data } = await requestPost(
            '/fulfillment-orders-dates-move-simulation',
            {
                variants: newTargetVariants,
                components: targetComponents,
                date: fullfillmentOrdersDate.date,
                location_id,
            }
        )

        setSourceDateGroup(data.source)
        setTargetDateGroup(data.target)
        setIsLoading(false)
    }

    const selectVariant = (variant) => {
        if (variant.variant_id) {
            // Check if variant is already selected
            if (targetVariants.find((id) => id === variant.variant_id)) {
                // If yes, remove it from the list
                const newTargetVariants = targetVariants.filter(
                    (id) => id !== variant.variant_id
                )

                setTargetVariants(newTargetVariants)
                fetchSimulations(newTargetVariants)
            } else {
                // If not, add it to the list
                const newTargetVariants = [
                    ...targetVariants,
                    variant.variant_id,
                ]

                setTargetVariants(newTargetVariants)
                fetchSimulations(newTargetVariants)
            }
        }
    }

    const selectComponent = (component) => {
        if (component.component_id) {
            // Check if component is already selected
            if (targetComponents.find((id) => id === component.component_id)) {
                // If yes, remove it from the list
                const newTargetComponents = targetComponents.filter(
                    (id) => id !== component.component_id
                )

                setTargetComponents(newTargetComponents)
                fetchSimulations(newTargetComponents)
            } else {
                // If not, add it to the list
                const newTargetComponents = [
                    ...targetComponents,
                    component.component_id,
                ]

                setTargetComponents(newTargetComponents)
                fetchSimulations(newTargetComponents)
            }
        }
    }

    // Item display
    const ItemWrapper = ({ children, selectable = true, onClick }) => {
        if (selectable) {
            return <Anchor onClick={onClick}>{children}</Anchor>
        }

        return <div>{children}</div>
    }

    const ItemDisplay = ({ item, selectable = true }) => {
        if (item.component_id) {
            return (
                <ItemWrapper
                    onClick={() => selectComponent(item)}
                    selectable={selectable}
                >
                    <div className="flex justify-between">
                        <span>
                            {item.component_category_name +
                                ' — ' +
                                item.component_name}
                        </span>

                        <span>×{item.total_remaining_quantity}</span>
                    </div>
                </ItemWrapper>
            )
        }

        return (
            <ItemWrapper
                onClick={() => selectVariant(item)}
                selectable={selectable}
            >
                <div className="flex justify-between">
                    <span>
                        {item.shopify_product_title +
                            ' - ' +
                            item.shopify_variant_title}
                    </span>

                    <span>×{item.total_remaining_quantity}</span>
                </div>
            </ItemWrapper>
        )
    }

    const sourceItemsSorted = orderBy(
        sourceDateGroup.items,
        ['total_remaining_quantity'],
        ['desc']
    )

    // Submit
    const submitNewDate = async () => {
        if (!targetDateValue) {
            return
        }

        requestPost('/fulfillment-orders-dates-move', {
            variants: targetVariants,
            components: targetComponents,
            source_date: fullfillmentOrdersDate.date,
            target_date: dayjs(targetDateValue).format('YYYY-MM-DD'),
            location_id,
        })

        handlers.close()

        onDateChange()
    }

    return (
        <>
            <Button variant="default" onClick={openModal}>
                Modifier la date
            </Button>

            <Modal
                title="Modifier la date des éléments sélectionnées"
                size={1300}
                opened={opened}
                onClose={handlers.close}
            >
                <div className="flex flex-col">
                    <div className="grid grid-cols-2 gap-5">
                        <div className="flex flex-col">
                            <span className="text-orange-500 font-medium">
                                {fullfillmentOrdersDate.date != 'null'
                                    ? dayjs(fullfillmentOrdersDate.date).format(
                                          'LL'
                                      )
                                    : 'Pas de date fixée'}
                            </span>

                            {!sourceDateGroup && (
                                <div className="mt-10 text-center text-gray-400 text-sm">
                                    Plus aucun produit à préparer pour cette
                                    date
                                </div>
                            )}

                            {sourceDateGroup && (
                                <>
                                    <div className="mt-5 relative">
                                        <LoadingOverlay visible={isLoading} />

                                        {sourceItemsSorted.map(
                                            (item, index) => {
                                                // If element is a component
                                                return (
                                                    <ItemDisplay
                                                        item={item}
                                                        key={index}
                                                    />
                                                )
                                            }
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex flex-col">
                            <span className="font-medium text-orange-500">
                                Date cible
                            </span>

                            {!targetDateGroup && (
                                <div className="mt-10 text-center text-gray-400 text-sm">
                                    Selectionner une variante à déplacer
                                </div>
                            )}

                            {targetDateGroup && (
                                <>
                                    <div className="mt-5 relative">
                                        <LoadingOverlay visible={isLoading} />

                                        {targetDateGroup.items.map(
                                            (item, index) => {
                                                const isSelected =
                                                    targetVariants.includes(
                                                        item.variant_id
                                                    ) ||
                                                    targetComponents.includes(
                                                        item.component_id
                                                    )

                                                return (
                                                    <ItemDisplay
                                                        item={item}
                                                        key={index}
                                                        selectable={isSelected}
                                                    />
                                                )
                                            }
                                        )}
                                    </div>

                                    <div className="mt-5 flex flex-col gap-1">
                                        <DatePickerInput
                                            locale="fr"
                                            placeholder="Choisir une date cible"
                                            valueFormat="LL"
                                            value={targetDateValue}
                                            onChange={setTargetDateValue}
                                            fullWidth
                                            popoverProps={{
                                                withinPortal: true,
                                                position: 'bottom',
                                            }}
                                        />

                                        <Button onClick={submitNewDate}>
                                            Modifier la date de préparations des
                                            ces variantes
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>

                        {false && (
                            <>
                                <pre>
                                    {JSON.stringify(targetVariants, null, 2)}
                                </pre>
                                <pre>
                                    {JSON.stringify(targetComponents, null, 2)}
                                </pre>
                            </>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    )
}
